import React, { Component } from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import {
  Grid,
  Table,
  Segment,
  Modal,
  Checkbox,
  Button,
  Icon,
  Pagination,
  Select,
  Input,
  Menu,
  Confirm,
} from "semantic-ui-react";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ContentLoader from "~/components/element/ContentLoader";
import CreditAccountForm from "./CreditAuditWidget/CreditAccountForm";
import CreditAuditStore from "../stores/CreditAuditStore";
import CreditAccountStore from "../stores/CreditAccountStore";
import CreditAccounts from "./CreditAuditWidget/CreditAccounts";
import FormStore from "~/stores/FormStore";

@observer
class CreditAuditWidget extends Component {
  @observable creditAuditStore = null;

  @observable creditAccountStore = null;

  @observable formStore = null;

  comparisonOperators = [
    { value: "!=", text: "not" },
    { value: "=", text: "equals" },
    { value: "<", text: "lower than" },
    { value: "<=", text: "lower or equal than" },
    { value: ">", text: "greater than" },
    { value: ">=", text: "greater or equal than" },
  ];

  recordsPerPageOptions = [25, 50, 100, 200];

  initialState = {
    isLoading: false,
    showCreditsAccount: false,
    selectedProvider: null,
    currentPage: 1,
    balanceFilter: 0,
    balanceComparisonOperator: "!=",
    recordsPerPage: 25,
    createCreditAccountData: null,
  };

  constructor(props) {
    super(props);
    this.creditAuditStore = new CreditAuditStore({ isLoading: true });
    this.creditAccountStore = new CreditAccountStore();

    this.state = this.initialState;

    this.formStore = new FormStore({
      balanceFilter: this.initialState.balanceFilter,
      balanceComparisonOperator: this.initialState.balanceComparisonOperator,
      recordsPerPage: this.initialState.recordsPerPage,
      currentPage: 1,
    });
  }

  componentDidMount() {
    const { showError } = this.props;
    const { recordsPerPage } = this.state;

    this.creditAuditStore.load({ limit: recordsPerPage, offset: 0 }).catch(error => showError(error));
  }

  getCreditAuditStoreQueryFromState() {
    const { recordsPerPage, balanceFilter, balanceComparisonOperator, currentPage } = this.state;

    const query = {
      limit: recordsPerPage,
      offset: (currentPage - 1) * recordsPerPage,
      comparison: balanceComparisonOperator,
    };

    const actualBalanceFilter = parseInt(balanceFilter, 10);

    if (Number.isNaN(actualBalanceFilter)) {
      throw new Error("Unexpected non integer value for balanceFilter");
    }

    query.balance = actualBalanceFilter;

    return query;
  }

  showCreditsAccount(selectedProvider) {
    const { recordsPerPage } = this.state;

    this.setState({ showCreditsAccount: true, selectedProvider });
    this.creditAccountStore.load({ providerId: selectedProvider.providerId, limit: recordsPerPage, offset: 0 });
  }

  closeCreditsAccount() {
    this.setState({ showCreditsAccount: false, selectedProvider: null });
  }

  deleteCreditAccount(creditAccountId) {
    const { showError } = this.props;
    const { selectedProvider } = this.state;
    const { records } = this.creditAuditStore;

    // we'll update the provider record's balance too, just right after deleting the credit account entry.
    const auditRecord = records.find(record => record.providerId === selectedProvider.providerId);

    return this.creditAccountStore
      .delete(creditAccountId)
      .then(() => {
        // update the audit record.
        // Note AuditRecord has no setter for balance.
        // creditAccountStore will take care of updating its balance, inline.
        auditRecord.balance = this.creditAccountStore.balance;
        this.reloadAppData();
      })
      .catch(error => {
        showError(error);

        throw error;
      });
  }

  updateCreditAccount(creditAccountId, data) {
    const { showError } = this.props;
    const { selectedProvider } = this.state;
    const { records } = this.creditAuditStore;

    // we'll update the provider record's balance too, just right after updating the credit account entry.
    const auditRecord = records.find(record => record.providerId === selectedProvider.providerId);

    // creditAccountStore will take care of deleting the record from backend, remove the entry from the store array
    // and fixing the balance inline.
    return this.creditAccountStore
      .update(creditAccountId, data)
      .then(() => {
        // Note AuditRecord has no setter for balance.
        // creditAccountStore will take care of updating its balance, inline.
        auditRecord.balance = this.creditAccountStore.balance;
        this.reloadAppData();
      })
      .catch(error => {
        showError(error);

        throw error;
      });
  }

  createCreditAccount(data) {
    const { showError } = this.props;

    const { selectedProvider } = this.state;
    const { records } = this.creditAuditStore;

    // we'll update the provider record's balance too, just right after updating the credit account entry.
    const auditRecord = records.find(record => record.providerId === selectedProvider.providerId);

    // creditAccountStore will take care of deleting the record from backend, remove the entry from the store array
    // and fixing the balance inline.
    return this.creditAccountStore
      .createCreditAccount(selectedProvider.providerId, data)
      .then(() => {
        // Note AuditRecord has no setter for balance.
        // creditAccountStore will take care of updating its balance, inline.
        auditRecord.balance = this.creditAccountStore.balance;
        this.reloadAppData();
      })
      .catch(error => {
        showError(error);

        throw error;
      });
  }

  reloadAppData() {
    const { showError } = this.props;

    return Promise.all([this.creditAccountStore.reload(false), this.creditAuditStore.reload(false)]).catch(error => {
      showError(error);

      throw error;
    });
  }

  changeCreditAuditPage(currentPage) {
    const { showError } = this.props;

    return this.setState({ currentPage }, () => {
      const query = this.getCreditAuditStoreQueryFromState();
      return this.creditAuditStore.load(query).catch(error => showError(error));
    });
  }

  changeCreditAccountsPage(currentPage) {
    const { showError } = this.props;
    const { selectedProvider, recordsPerPage } = this.state;

    return this.creditAccountStore
      .load({
        providerId: selectedProvider.providerId,
        limit: recordsPerPage,
        offset: (currentPage - 1) * recordsPerPage,
      })
      .catch(error => showError(error));
  }

  jumpToPage() {
    const { model, setProperty } = this.formStore;

    let currentPage = parseInt(model.currentPage, 10);
    currentPage = Number.isNaN(currentPage) ? 1 : currentPage;
    setProperty("currentPage", currentPage);

    return this.changeCreditAuditPage(currentPage);
  }

  filterResults() {
    const { model, setProperty } = this.formStore;

    // If model.balanceFilter is empty and the user clicks the filter button then we'll interpret it as if the user
    // wants to clear the filter and do a clean search.
    if (Number.isNaN(parseInt(model.balanceFilter, 10))) {
      this.setState({
        balanceFilter: this.initialState.balanceFilter,
        balanceComparisonOperator: this.initialState.balanceComparisonOperator,
      });

      setProperty("balanceFilter", this.initialState.balanceFilter);
      setProperty("balanceComparisonOperator", this.initialState.balanceComparisonOperator);

      return this.changeCreditAuditPage(1);
    }

    const actualBalanceFilter = parseInt(model.balanceFilter, 10);

    if (Number.isNaN(actualBalanceFilter)) {
      setProperty("balanceFilter", "error");

      return Promise.resolve();
    }

    return this.setState(
      {
        recordsPerPage: parseInt(model.recordsPerPage, 10),
        balanceFilter: actualBalanceFilter,
        balanceComparisonOperator: model.balanceComparisonOperator,
      },
      () => {
        return this.changeCreditAuditPage(1);
      },
    );
  }

  showConfirmCreateCreditAccounts(createCreditAccountData) {
    this.setState({ createCreditAccountData });
  }

  cancelCreateCreditAccounts() {
    this.setState({ createCreditAccountData: null });
  }

  createCreditAccounts() {
    const { showError } = this.props;
    const { createCreditAccountData } = this.state;
    const { records } = this.creditAuditStore;

    const selectedRecords = records.filter(record => record.isSelected);

    if (selectedRecords.length === 0) {
      this.cancelCreateCreditAccounts();
      return false;
    }

    const data = [];

    selectedRecords.forEach(record => {
      data.push({
        ...createCreditAccountData,
        providerId: record.providerId,
      });
    });

    this.setState({ createCreditAccountData: null, isLoading: true });

    return this.creditAccountStore
      .createCreditAccounts(data)
      .then(() => {
        // whenever we bulk create credit accounts, let's go back to page one (which will reload the results too)
        return this.changeCreditAuditPage(1);
      })
      .catch(error => showError(error))
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  render() {
    const {
      showCreditsAccount,
      selectedProvider,
      currentPage,
      balanceFilter,
      balanceComparisonOperator,
      recordsPerPage,
      createCreditAccountData,
      isLoading,
    } = this.state;

    const {
      isLoading: creditAuditStoreIsLoading,
      records,
      total: totalCreditAuditRecords,
      allSelected,
    } = this.creditAuditStore;

    const {
      isLoading: creditAccountStoreIsLoading,
      creditAccounts,
      balance,
      total: totalCreditAccounts,
    } = this.creditAccountStore;

    const { model, setProperty } = this.formStore;

    // For display, there should always be one page at least.
    const totalPages = Math.max(Math.ceil(totalCreditAuditRecords / recordsPerPage), 1);
    // If for any reason the current page is bigger than the total pages, then default to the last page.
    const actualActivePage = Math.min(currentPage, totalPages);

    const pageOptions = [];

    for (let i = 1; i <= totalPages; i += 1) {
      pageOptions.push({ key: i, value: i, text: i });
    }

    const selectedRecords = records.filter(record => record.isSelected);

    return (
      <LoadingWrapper loading={creditAuditStoreIsLoading || isLoading}>
        {createCreditAccountData && (
          <Confirm
            open
            content={
              selectedRecords.length > 0
                ? `Confirm adding amount: ${createCreditAccountData.amount}, description:
                ${createCreditAccountData.description} to (${selectedRecords.length}) records.`
                : "No records selected"
            }
            onConfirm={() => this.createCreditAccounts()}
            onCancel={() => this.cancelCreateCreditAccounts()}
          />
        )}
        <Grid>
          <Grid.Row>
            <Grid.Column textAlign="center">
              <Segment>
                <Menu>
                  <Menu.Item>
                    <Select
                      value={model.recordsPerPage}
                      options={this.recordsPerPageOptions.map(option => ({ key: option, value: option, text: option }))}
                      onChange={(_event, { value }) => setProperty("recordsPerPage", parseInt(value, 10))}
                    />
                    &nbsp;
                    <Button basic color="blue" onClick={() => this.filterResults()}>
                      Show
                    </Button>
                  </Menu.Item>
                  <Menu.Item>
                    <Select
                      placeholder="page"
                      value={model.currentPage}
                      options={pageOptions}
                      onChange={(_event, { value }) => setProperty("currentPage", value)}
                    />
                    &nbsp;
                    <Button basic color="blue" onClick={() => this.jumpToPage()}>
                      Jump
                    </Button>
                  </Menu.Item>
                  <Menu.Item>
                    <span>Balance</span>
                    &nbsp;
                    <Select
                      value={model.balanceComparisonOperator}
                      onChange={(_event, { value }) => setProperty("balanceComparisonOperator", value)}
                      options={this.comparisonOperators.map(({ value, text }) => ({
                        key: value,
                        value,
                        text,
                      }))}
                    />
                    &nbsp;
                    <Input
                      value={model.balanceFilter}
                      placeholder="balance: 18"
                      onChange={event => setProperty("balanceFilter", event.target.value)}
                    />
                    &nbsp;
                    <Button basic color="blue" onClick={() => this.filterResults()}>
                      Filter
                    </Button>
                  </Menu.Item>
                </Menu>
                <p>
                  Showing {records.length} items. Page {actualActivePage}/{totalPages}.{" "}
                  {`Filtering by balance ${balanceComparisonOperator} ${balanceFilter}.`}
                </p>
                <Pagination
                  activePage={actualActivePage}
                  totalPages={totalPages}
                  onPageChange={(_event, { activePage }) => this.changeCreditAuditPage(activePage)}
                />
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>
                        <Checkbox checked={allSelected} onChange={() => this.creditAuditStore.toggleSelectAll()} />
                      </Table.HeaderCell>
                      <Table.HeaderCell>provider id</Table.HeaderCell>
                      <Table.HeaderCell>name</Table.HeaderCell>
                      <Table.HeaderCell>company</Table.HeaderCell>
                      <Table.HeaderCell>email</Table.HeaderCell>
                      <Table.HeaderCell>last balance update</Table.HeaderCell>
                      <Table.HeaderCell>current balance</Table.HeaderCell>
                      <Table.HeaderCell />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {records.map(record => (
                      <Table.Row key={record.providerId}>
                        <Table.Cell>
                          <Checkbox
                            checked={record.isSelected}
                            onChange={() => this.creditAuditStore.toggleSelected(record.providerId)}
                          />
                        </Table.Cell>
                        <Table.Cell>{record.providerId}</Table.Cell>
                        <Table.Cell>{record.name}</Table.Cell>
                        <Table.Cell>{record.company}</Table.Cell>
                        <Table.Cell>{record.email}</Table.Cell>
                        <Table.Cell>{record.balanceUpdatedDts.format("YYYY-MM-DD HH:mm:ss")}</Table.Cell>
                        <Table.Cell>{record.balance}</Table.Cell>
                        <Table.Cell>
                          <Button primary onClick={() => this.showCreditsAccount(record)}>
                            details
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
                <Pagination
                  activePage={actualActivePage}
                  totalPages={totalPages}
                  onPageChange={(_event, { activePage }) => this.changeCreditAuditPage(activePage)}
                />
              </Segment>
              <Segment>
                <CreditAccountForm onSubmit={data => this.showConfirmCreateCreditAccounts(data)} />
              </Segment>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {/* Modal and CreditAccounts requires a selected provider */}
        {selectedProvider && (
          <Modal open={showCreditsAccount} onClose={() => this.closeCreditsAccount()} size="fullscreen">
            <Modal.Header>
              Credits for #{selectedProvider.providerId} {selectedProvider.name}, ({selectedProvider.company})
            </Modal.Header>
            <Modal.Content image scrolling>
              <Modal.Description>
                <CreditAccounts
                  balance={balance}
                  creditAccounts={creditAccounts}
                  totalCreditAccounts={totalCreditAccounts}
                  recordsPerPage={recordsPerPage}
                  onDelete={creditAccountId => this.deleteCreditAccount(creditAccountId)}
                  onUpdate={(creditAccountId, data) => this.updateCreditAccount(creditAccountId, data)}
                  onCreate={data => this.createCreditAccount(data)}
                  onPageChange={activePage => this.changeCreditAccountsPage(activePage)}
                />
              </Modal.Description>
            </Modal.Content>
            <Modal.Actions>
              <Button onClick={() => this.closeCreditsAccount()} primary>
                Close <Icon name="close" />
              </Button>
              <ContentLoader isLoading={creditAccountStoreIsLoading} />
            </Modal.Actions>
          </Modal>
        )}
      </LoadingWrapper>
    );
  }
}

export default ErrorWrapper(CreditAuditWidget);
